/**
 * 真实后端客户端（对接你自己的 Python 服务）。
 * 路径风格沿用参考站的契约，去掉 /__pb 前缀（那是别人的 PocketBase 反代）。
 *
 * 你需要在 Python 端实现的路由（最小可跑 5 条）：
 *   GET  /api/me
 *   POST /api/aigc/upload
 *   POST /api/aigc/price-preview
 *   POST /api/aigc/ai-app/{appSlug}/run
 *   POST /api/aigc/ai-app/{appSlug}/jobs/{jobId}/poll
 * 以及资产/作品/用量：
 *   GET/POST/DELETE /api/assets            GET/POST/PATCH/DELETE /api/generated_works
 *   GET  /api/asset-library (+ /stats)     资产库统一视图（作品 + 上传素材）
 *   GET  /api/usage/records
 */
import {
  clearTokenOf,
  emitUnauthorized,
  getToken,
  tokenOf,
  type Principal,
} from './session'
import type {
  AdminLoginResponse,
  AdminOverview,
  AdminUser,
  ApiClient,
  AssetItem,
  AssetLibraryItem,
  AssetLibraryStats,
  AuthUser,
  CoinLedgerItem,
  CreateAdminRequest,
  CreateUserRequest,
  CreateVideoTemplateRequest,
  CustomVideoTemplate,
  AnalyzeVideoRequest,
  AnalyzeVideoResponse,
  FillFormRequest,
  FillFormResponse,
  GenerateRequest,
  Identity,
  LoginResponse,
  ModelFamilyInfo,
  OutputKind,
  PollResponse,
  PricePreviewRequest,
  PricePreviewResponse,
  ProductAsset,
  ProductAssetFacets,
  ProductAssetQuery,
  CreateProductAssetRequest,
  UpdateProductAssetRequest,
  BulkCreateProductAssetsResponse,
  ProductCategory,
  RenameProductCategoryResponse,
  RefinePromptRequest,
  RefinePromptResponse,
  RunResponse,
  ServerJobItem,
  SkillIndex,
  UpdateAdminRequest,
  UpdateVideoTemplateRequest,
  UploadResult,
  UpstreamCatalog,
  UpstreamCatalogResult,
  UpstreamCreate,
  UpstreamUpdate,
  UsageSummary,
  UpdateUserRequest,
  VoiceCatalog,
  VoicePreviewRequest,
  VoicePreviewResponse,
  WorkItem,
} from './types'

const BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  code: string
  status: number
  constructor(message: string, code = 'UNKNOWN', status = 0) {
    super(message)
    this.code = code
    this.status = status
  }
}

/**
 * 请求超时（毫秒）。**没有这道闸，后端一僵死 UI 就只能干转圈。**
 *
 * 2026-09-29 的故障正是这个：uvicorn 的 `--reload` 监督进程卡在
 * 「杀不掉旧 worker」的等待里，监听端口还攥在它手上但不 accept ——
 * TCP 三次握手能成（connect 不报错），HTTP 响应一个字节都不回。
 * 裸 `fetch` 找不到任何超时依据，于是上传框永远停在 spinner 上，
 * 用户看到的是「上传坏了」，而不是「后端没响应」。
 *
 * 普通接口的后端耗时都是毫秒级（提交只是入队，生成走后台线程池），20s 足够宽。
 */
const TIMEOUT_MS = 20_000

/**
 * 上传超时单独放宽：后端上传一波要做「落盘 + 发图床/COS」，
 * 而它自己的最长等待是 COS 300s（`HW_COS_TIMEOUT`）/ ImgBB 120s。
 * 前端若短于这个数，就会把后端**本来会成功**的大文件上传误判成失败。
 *
 * 所以真正负责「后端死了要立刻报错」的不是这个数，而是前面那个 20s ——
 * 页面一进来就会打好几个接口（任务列表 / 报价 / 模型），后端僵死时
 * 那几条会先报出来；再加上上传前的预检（见 AssetLibraryContext.upload）。
 */
const UPLOAD_TIMEOUT_MS = 300_000

/**
 * 「让 LLM 改输入」类端点的超时（`refinePrompt` / `fillForm`）。
 *
 * 这两个调用都是**同步等 LLM 出正文**（一份分镜提示词 500-1500 字、四个文案字段
 * 也要几秒到十几秒，上游排队时更久），不是「提交即返回」。套 20s 那道闸会把
 * **本来会成功**的调用判成 TIMEOUT；而用户看到「后端没响应」，第一反应是去重启
 * 一个完全健康的后端。2 分钟够覆盖正常与排队两种情形，同时仍能在真挂住时给结论。
 *
 * 两条端点共用一份常量：它们是同一类动作（一次同步 LLM 调用），
 * 分开写两个数就是同一份判断写两处 —— 改一处必漏另一处。
 */
const LLM_CALL_TIMEOUT_MS = 120_000

/** 带超时的 fetch。超时抛 AbortError，由调用方翻成人话。 */
async function fetchWithTimeout(url: string, init: RequestInit, ms: number): Promise<Response> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  try {
    return await fetch(url, { ...init, signal: ctrl.signal })
  } finally {
    clearTimeout(timer)
  }
}

/**
 * AbortError → 超时文案。
 *
 * 长活（上传 / 提示词改写）与普通接口（20s）**归因完全不同**：20s 没响应基本
 * 只能是后端僵死；而长活走满超时，说明这一步本身就慢。用同一句「多半是进程僵死」
 * 解释后者，会把用户支向错误方向 —— 去重启一个健康的后端，再重跑一遍同样的慢活。
 */
function timeoutError(ms: number): ApiError {
  const long = ms > 60_000
  return new ApiError(
    long
      ? `请求超时：${ms / 1000}s 内没有返回。这一步本身就可能跑很久（模型推理 / 上传），` +
          `不一定是后端故障 —— 先看任务中心顶栏还亮不亮「后端正常」，再重试一次。`
      : `请求超时：${ms / 1000}s 内后端没有任何响应。多半是后端进程僵死（不是网络问题）—— ` +
          `重启后端即可（cd onevideo/backend && .venv/Scripts/python run.py）`,
    'TIMEOUT',
    0,
  )
}

function isAbort(e: unknown): boolean {
  return (e as { name?: string } | null)?.name === 'AbortError'
}

/**
 * 这条请求属于**哪一侧**（会员 / 后台）。
 *
 * 🔴 **判据只有路径前缀一条，不由调用方声明**。理由：两套身份各有一个 token，
 * 谁该带哪个是**协议事实**（后端 `sessions.principal` 决定），不是调用方的偏好。
 * 让每个 API 方法自己标一下「我是后台的」，加第 15 个后台方法时漏一个 ——
 * 症状是那个接口拿会员 token 打过去、返回 401，而控制台里看不出哪里不对。
 *
 * `/api/admin/*` 全部是后台侧（含 `/api/admin/auth/login`），其余都是会员侧。
 * 这个前缀在**同一个仓库的同一份契约里**，不是两边各记一份的约定：
 * 后端后台路由的 prefix 就是 `/api/admin`（见 `app/routers/admin.py`）。
 */
function principalOf(path: string): Principal {
  return path.startsWith('/api/admin') ? 'admin' : 'user'
}

/** 两个登录端点：它们返回 401 是「密码错」，**不能**当成掉线广播出去。 */
const LOGIN_PATHS = ['/api/auth/login', '/api/admin/auth/login']

/**
 * 会话 token 要**同时**放进两个头。
 *
 * 🔴 `X-HW-Token` 才是云端真正生效的那个：WorkBuddy 云托管（「发布应用」）会在入口
 * 把**每个请求**的 `Authorization` 改写成平台自己的 JWT（2026-10-09 实测：应用收到的是
 * `Bearer eyJhb…`、长度 386，客户端传什么、甚至不传都是这个值）。于是我们自己的 token
 * 永远到不了后端 —— 表现是「登录成功、之后每个接口都 401」，而健康检查是好的，
 * 所以第一眼会以为是后端坏了。自定义头实测原样透传。
 *
 * 为什么还留着 `Authorization`：本机开发、`curl`、以及没有托管层的部署都靠它；
 * 后端对「一枚来历不明的 JWT」按「没带」处理，两条路不会互相干扰。
 *
 * ⚠️ 只此一份实现 —— 普通请求与 `upload()` 都从这里取，别各写一遍。
 */
export function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'X-HW-Token': token }
}

/**
 * @param timeoutMs 覆盖默认闸门。**只在「后端合法地慢」的接口上放宽**
 *   （目前只有 `refinePrompt` 一个）—— 别拿它给普通接口加时间，
 *   20s 那道闸的全部价值就是让后端僵死时页面尽快说实话。
 */
async function request<T>(path: string, init?: RequestInit, timeoutMs = TIMEOUT_MS): Promise<T> {
  // 头要用 `Headers` 现拼，不能写成字面量再 `...init` 展开：
  // `...init` 在字面量之后，`init.headers` 会把整份头**换掉**（而不是合并），
  // 于是 caller 传一次 headers，Content-Type 和 Authorization 就都没了。
  const headers = new Headers(init?.headers as HeadersInit | undefined)
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  const principal = principalOf(path)
  const token = tokenOf(principal)
  if (token) {
    for (const [k, v] of Object.entries(authHeaders(token))) {
      // 逐键判断而不是整份覆盖：caller 显式传过的头要保留（原行为）。
      if (!headers.has(k)) headers.set(k, v)
    }
  }

  let res: Response
  try {
    res = await fetchWithTimeout(
      `${BASE}${path}`,
      { credentials: 'include', ...init, headers },
      timeoutMs,
    )
  } catch (e) {
    if (isAbort(e)) throw timeoutError(timeoutMs)
    throw new ApiError('网络异常，请检查后端是否已启动', 'NETWORK', 0)
  }

  if (!res.ok) {
    let code = `HTTP_${res.status}`
    let message = res.statusText || '请求失败'
    let parsed = false
    try {
      const body = await res.json()
      parsed = true
      code = body?.detail?.code ?? body?.error?.code ?? code
      message = body?.detail?.message ?? body?.error?.message ?? body?.message ?? message
    } catch {
      /* 非 JSON 响应，保留默认信息 */
    }

    /*
     * 5xx + **响应体不是 JSON** = 我们自己的后端根本没出这个响应。
     *
     * 真后端报 5xx 一定带 JSON（FastAPI 的 `{"detail": ...}`），所以这个组合
     * 只可能来自中间那一层：dev 时是 Vite 的 proxy —— 后端进程不在（没启动 /
     * 已经退出），proxy 转发失败，回一个纯文本 `Internal Server Error`（500）。
     *
     * 2026-10-09 用户报障：「商品库加载失败 · Internal Server Error。确认后端已启动
     * （默认 127.0.0.1:8000）」—— 这句话把后端状态原样丢给用户，既没说清是什么坏了，
     * 也没说该做什么。而它实际上是**最好修**的一类故障（起个进程就好）。
     * 所以这里就地翻成人话，并且把命令写出来。
     */
    if (!parsed && res.status >= 500) {
      code = 'BACKEND_UNREACHABLE'
      message =
        '连不上后端 —— 127.0.0.1:8000 没有进程在服务（前端开发服务器的转发被拒）。' +
        '启动后端后再重试：cd onevideo/backend && .venv/Scripts/python run.py'
    }
    // 401 就地清 **那一侧** 的 token 并广播：否则 token 过期后界面停在原地
    // 不报错也回不去，用户只看到一堆「登录已过期」，不知道要做什么。
    //
    // ⚠️ 两个「别清」的边界：
    //   ① 登录端点自己会返回 401（密码错）—— 广播 = 「你被登出了」，
    //      而此刻用户本来就是未登录态，弹这个只会让人以为密码错是掉线。
    //   ② 只清 `principal` 那一侧。清错一侧的症状是：在后台点一下，
    //      工作台的顶栏突然变成「未登录」—— 用户完全不知道自己做错了什么。
    if (res.status === 401 && !LOGIN_PATHS.includes(path)) {
      clearTokenOf(principal)
      emitUnauthorized(principal)
    }
    throw new ApiError(message, code, res.status)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** 统一路径 404 时回退到模型作用域路径（参考站的前端策略） */
async function postWithFallback<T>(primary: string, fallback: string, body: unknown): Promise<T> {
  try {
    return await request<T>(primary, { method: 'POST', body: JSON.stringify(body) })
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return request<T>(fallback, { method: 'POST', body: JSON.stringify(body) })
    }
    throw e
  }
}

export const httpClient: ApiClient = {
  mode: 'http',

  me: () => request<Identity>('/api/me'),

  /**
   * 模型清单。取 `families` 而不是 `models`——后者是底层路由 slug 表，
   * 含 20 个未接入上游和 6 个同模型别名，渲染出来就是一屏假选项。
   */
  async listModels(kind?: 'image' | 'video' | 'audio' | 'text'): Promise<ModelFamilyInfo[]> {
    const data = await request<{ families?: ModelFamilyInfo[] }>('/api/aigc/models')
    const list = data.families ?? []
    if (!list.length) throw new ApiError('模型清单为空', 'EMPTY_MODELS', 200)
    return kind ? list.filter((f) => f.kind === kind) : list
  },

  async listSkills(): Promise<SkillIndex> {
    // 后端关着时也会正常返回（enabled:false + 清单照给），**不要在这里抛错** ——
    // 「关」是正常状态，抛错会让调用方分不清「没开」和「后端挂了」。
    return request<SkillIndex>('/api/aigc/skills')
  },

  async listVoices(): Promise<VoiceCatalog> {
    // 与 listModels 不同，**这里不做「空清单就抛错」**：真源是 skill 里的音色表，
    // 空清单只可能是后端配置坏了，而页面上的正确反应是降级到内置兜底清单
    // （见 useVoices），不是把整个数字人页卡住。
    return request<VoiceCatalog>('/api/aigc/voices')
  },

  /**
   * 音色试听。
   *
   * ⚠️ 与 `refinePrompt` / `fillForm` **同规矩，不走 `postWithFallback`**：
   * 它的 fallback 是「同一个活换个路径再打一次」，而这条端点**首次会真花钱**
   * （调 TTS 合成 + 落 usage）—— 一旦「第一次真跑成功、响应却丢了」，
   * 重打就是白合成一次。宁可让老后端直接报 404 让人看见。
   *
   * 超时借用 `LLM_CALL_TIMEOUT_MS`：同一类动作（**同步等上游出结果**），
   * TTS 一次合成几秒到十几秒（上游排队时更久），20s 那道闸会把本来会成功的
   * 调用判成超时。缓存命中的那次是本地文件，几毫秒就回来。
   */
  previewVoice: (req: VoicePreviewRequest) =>
    request<VoicePreviewResponse>(
      '/api/aigc/voices/preview',
      { method: 'POST', body: JSON.stringify(req) },
      LLM_CALL_TIMEOUT_MS,
    ),

  async ping(): Promise<void> {
    // 走 request 的 20s 短超时：后端僵死时这里最快给出「无响应」，而不是让
    // 上传带着 5 分钟超时去慢慢等。健康检查在后端是纯内存计算（毫秒级）。
    await request<unknown>('/api/aigc/health')
  },

  async upload(file: File): Promise<UploadResult> {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('fileType', file.type.startsWith('video') ? 'video' : file.type.startsWith('audio') ? 'audio' : 'image')
    // 带超时：后端僵死时这里能给出「超时」而不是让 spinner 转到天荒地老
    // ⚠️ 这条不走 `request()`（FormData 不能带 JSON 的 Content-Type），
    // 所以会话头得自己加一份 —— 后端的上传端点已要求登录。
    // 走 `authHeaders()` 而不是手写 `Authorization`：云端托管层会改写那个头，
    // 手写一遍就等于「上传在云上永远 401」（见 `authHeaders` 的注释）。
    let res: Response
    try {
      const upToken = getToken()
      res = await fetchWithTimeout(
        `${BASE}/api/aigc/upload`,
        {
          method: 'POST',
          body: fd,
          credentials: 'include',
          headers: upToken ? authHeaders(upToken) : undefined,
        },
        UPLOAD_TIMEOUT_MS,
      )
    } catch (e) {
      if (isAbort(e)) throw timeoutError(UPLOAD_TIMEOUT_MS)
      throw new ApiError('上传失败：网络异常，请检查后端是否已启动', 'NETWORK', 0)
    }
    if (!res.ok) {
      // 401 与 request() 同规矩：清 token 并广播，否则上传会一直报「登录已过期」
      // 而界面还停在原地显示「已登录」。
      // ⚠️ 上传是**会员侧**的动作（`/api/aigc/*`），所以清的是会员 token ——
      // 不能顺手把后台那份也清掉（上传失败不该把开着的后台踢下线）。
      if (res.status === 401) {
        clearTokenOf('user')
        emitUnauthorized('user')
      }
      const txt = await res.text().catch(() => '')
      throw new ApiError(txt || '上传失败', `HTTP_${res.status}`, res.status)
    }
    const json = await res.json()
    // 后端历史上同时返回过 downloadUrl / download_url 两种命名
    const downloadUrl: string = json.downloadUrl ?? json.download_url ?? json.url
    if (!downloadUrl) throw new ApiError('上传接口未返回文件地址', 'BAD_RESPONSE', 200)
    return {
      downloadUrl,
      localUrl: json.localUrl ?? json.local_url ?? undefined,
      remoteUrl: json.remoteUrl ?? json.remote_url ?? null,
      host: json.host ?? '',
      fileName: json.fileName ?? json.file_name ?? file.name,
      size: file.size,
      mime: file.type,
      width: json.width,
      height: json.height,
    }
  },

  pricePreview: (req: PricePreviewRequest) =>
    postWithFallback<PricePreviewResponse>(
      '/api/aigc/price-preview',
      `/api/aigc/${req.model}/price-preview`,
      req,
    ),

  /**
   * 提示词改写。
   *
   * 为什么**不走** `postWithFallback`（别的接口都走）：它的 fallback 是
   * 「同一个活换个路径再打一次」。在生成任务上这没问题（第一次 404 = 老后端
   * 不认这个路径，重打一次不会有副作用）；但这个端点是**按次计费**的 ——
   * 一旦出现「第一次真跑成功、响应却丢了」，重打就是白扣一次钱。
   * 宁可在老后端上直接报 404 让人看见，也不要静默花双份。
   */
  refinePrompt: (req: RefinePromptRequest) =>
    request<RefinePromptResponse>(
      '/api/aigc/prompt/refine',
      { method: 'POST', body: JSON.stringify(req) },
      LLM_CALL_TIMEOUT_MS,
    ),

  /**
   * 表单填充（`/banner` 的「AI 填充」）。
   *
   * 与 `refinePrompt` 同规矩：**不走 `postWithFallback`**。它的 fallback 是
   * 「同一个活换个路径再打一次」，在生成任务上没副作用；但这条端点按次计费 ——
   * 一旦「第一次真跑成功、响应却丢了」，重打就是白扣一毛。宁可让它直接报 404
   * 让人看见「后端太老」，也不要静默花双份钱。
   */
  fillForm: (req: FillFormRequest) =>
    request<FillFormResponse>(
      '/api/aigc/form/fill',
      { method: 'POST', body: JSON.stringify(req) },
      LLM_CALL_TIMEOUT_MS,
    ),

  /**
   * 参考视频分析（视频复刻第 1 页的「分析视频」）。
   *
   * 与上两条同规矩，**不走 `postWithFallback`**：它的 fallback 是「同一个活换个
   * 路径再打一次」，而这条端点是**按次计费**的（¥0.30/次，比文案贵三倍）——
   * 一旦「第一次真跑成功、响应却丢了」，重打就是白扣三毛。
   * 宁可让老后端直接报 404 让人看见，也不要静默花双份。
   *
   * 超时借用 `LLM_CALL_TIMEOUT_MS`：同一类动作（**同步等上游出结果**）。
   * 实测一次 12 秒（上游要先把整条视频拉过去读），20s 那道闸会把它掐断 ——
   * 而用户看到「后端没响应」的第一反应是去重启一个完全健康的后端。
   */
  analyzeVideo: (req: AnalyzeVideoRequest) =>
    request<AnalyzeVideoResponse>(
      '/api/aigc/video/analyze',
      { method: 'POST', body: JSON.stringify(req) },
      LLM_CALL_TIMEOUT_MS,
    ),

  run: (req: GenerateRequest) =>
    postWithFallback<RunResponse>(
      `/api/aigc/ai-app/${req.appSlug}/run`,
      `/api/aigc/${req.model}/submit`,
      {
        ...req.inputs,
        // ⚠️ 顶层字段必须逐个显式列出，"只塞进 inputs" 不会被后端自动提升。
        // 后端 GenerateRequest 是多词字段显式 alias 解析的，且单字段名
        // （prompt/model/resolution）恰好两边同名 —— 所以漏掉 imageUrls 时
        // 不会报错，只会静默变成纯文生图（参考图被无视，产品外观完全不对）。
        // 踩过一次：传草绿色帽子出来藏蓝色 NY 棒球帽，任务还显示"已完成"。
        imageUrls: req.imageUrls,
        /*
         * ⚠️ **参考视频与参考图并列，同样必须显式列出**（2026-10-06 补）。
         *
         * 漏掉它**不报任何错**，而且症状比 `imageUrls` 那次更隐蔽：`videoUrls`
         * 被丢掉后，`...req.inputs` 展开时带上的表单字段 `refVideo` 会落到顶层，
         * 而后端 `routers/aigc.py` 的兜底恰好认 `refVideo` 这个名字 ——
         * 于是「能跑通」**纯属命名巧合**。哪天有人把表单字段改名
         * （`form.refVideo` → `form.refVideos`），参考视频会**静默消失**：
         * 上游按纯文生视频跑，任务照样 succeeded、钱照扣，界面上一切正常。
         *
         * 是探针 `scripts/verify-ref-video-prompt.py` 抓出来的 ——
         * 它读的是**确认弹窗里那份真会发出去的载荷**，不是页面状态。
         */
        videoUrls: req.videoUrls,
        prompt: req.prompt,
        // 逐张提示词：套图必须按张拆开发，否则 N 张全出成多视角拼贴
        ...(req.prompts?.length ? { prompts: req.prompts } : {}),
        // 逐张尺寸：A+ 各模块官方尺寸不同（970×600 / 970×300 / 300×300 …），
        // 与 prompts 一一对应。不带它 = 整批一个比例 = 一半模块错版。
        ...(req.ratios?.length ? { ratios: req.ratios } : {}),
        model: req.model,
        ...(req.aspectRatio ? { aspectRatio: req.aspectRatio } : {}),
        ...(req.resolution ? { resolution: req.resolution } : {}),
        ...(req.outputs ? { outputs: req.outputs } : {}),
        ...(req.estimatedMs ? { estimatedMs: req.estimatedMs } : {}),
        ...(req.sourcePage ? { sourcePage: req.sourcePage } : {}),
        ...(req.title ? { title: req.title } : {}),
        // platform 是后端要落库的追溯字段，显式带上 ——
        // 不然它会随 inputs 的字段名变动而悄悄丢失
        ...(req.platform ? { platform: req.platform } : {}),
        // 批次 id：批量表格的 N 个 job 共用它，后端只落库不参与执行。
        // 同样必须显式列出（跟 imageUrls 一个道理：只塞进 inputs 不会被提升）。
        // 漏了它**不报任何错** —— 每个 job 的 batch_id 都是空串，
        // 右栏把一批 20 个产品渲染成 20 张互不相干的卡，只是分组丢了。
        ...(req.batchId ? { batchId: req.batchId } : {}),
      },
    ),

  poll: (appSlug: string, jobId: string) =>
    postWithFallback<PollResponse>(
      `/api/aigc/ai-app/${appSlug}/jobs/${jobId}/poll`,
      `/api/aigc/jobs/${jobId}/poll`,
      {},
    ),

  // 取消失败不抛错：本地该标的已经标了，后端多用几轮轮询换不来报错弹窗
  cancel: (appSlug: string, jobId: string) =>
    request<void>(`/api/aigc/ai-app/${appSlug}/jobs/${jobId}/cancel`, { method: 'POST' }).catch(
      () => undefined,
    ),

  async listJobs(params) {
    const q = new URLSearchParams({ limit: String(params?.limit ?? 100) })
    if (params?.state && params.state !== 'all') q.set('state', params.state)
    // 可见性口径默认由**后端**决定（exclude = 不含已清理）。这里只在调用方
    // 明确要看「已清理 / 全部」时才显式传，避免前端再维护一份「默认是什么」。
    if (params?.hidden && params.hidden !== 'exclude') q.set('hidden', params.hidden)
    const json = await request<{ items?: ServerJobItem[]; hiddenCount?: number }>(
      `/api/aigc/jobs?${q.toString()}`,
    )
    return {
      items: (json.items ?? []).map((it) => ({
        ...it,
        outputs: it.outputs ?? [],
        imageUrls: it.imageUrls ?? [],
        inputs: it.inputs ?? {},
        error: it.error ?? null,
      })),
      // 已清理总数由后端给（与 limit 无关）—— 前端自己数会被截断
      hiddenCount: json.hiddenCount ?? 0,
    }
  },

  /**
   * 清理 / 恢复任务记录。
   *
   * **不删行**（后端只置 `hidden`）：任务的账要留着复盘、按原参数重试。
   * 返回真正翻转的条数（已经是目标状态的不算），调用方据此给提示。
   * 失败不抛：清理是界面动作，后端不通时前端本地已经不在列表里了，
   * 弹一个「取消失败」反而像是操作没生效。
   */
  /**
   * 清理 / 恢复一堆记录 —— **失败必须抛**，不许吞成 `changed: 0`。
   *
   * 调用方（`JobCenterContext`）靠「抛没抛」区分两件事：
   *   ① `migrateLegacyHidden` 只有在**确认落库成功**后才敢删掉浏览器里的
   *      老黑名单 —— 吞错的话那次迁移会「本地键删了、库里没写」，
   *      被清理过的记录集体复活，而且一声不响；
   *   ② `hideRecords` 是「先动界面、再落库」，落库失败要撤回并告诉用户。
   * 吞掉错误 = 界面显示已清理、库其实没改 = 下次刷新又冒出来（第二份真源的经典症状）。
   *
   * 后端对**未知 id** 本来就是静默忽略（返回 `changed: 0` 且不报错），
   * 所以「老黑名单里有已失效的 id」不会变成异常，这一点不受影响。
   */
  hideJobs: async (jobIds: string[]) => {
    const json = await request<{ changed?: number }>('/api/aigc/jobs/hide', {
      method: 'POST',
      body: JSON.stringify({ jobIds }),
    })
    return json.changed ?? 0
  },

  unhideJobs: async (jobIds: string[]) => {
    const json = await request<{ changed?: number }>('/api/aigc/jobs/unhide', {
      method: 'POST',
      body: JSON.stringify({ jobIds }),
    })
    return json.changed ?? 0
  },

  async listWorks(params) {
    const q = new URLSearchParams({
      page: String(params?.page ?? 1),
      perPage: String(params?.perPage ?? 200),
    })
    if (params?.workType) q.set('work_type', params.workType)
    if (params?.category) q.set('category', params.category)
    if (params?.origin) q.set('origin', params.origin)
    const json = await request<{ items: WorkItem[]; total?: number; total_items?: number }>(
      `/api/generated_works?${q.toString()}`,
    )
    return { items: json.items ?? [], total: json.total ?? json.total_items ?? (json.items ?? []).length }
  },

  deleteWork: (id: string) => request<void>(`/api/generated_works/${id}`, { method: 'DELETE' }),

  toggleFavorite: (id: string, favorite: boolean) =>
    request<void>(`/api/generated_works/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ favorite }),
    }),

  async listAssets() {
    const json = await request<{ items: AssetItem[] } | AssetItem[]>('/api/assets')
    return Array.isArray(json) ? json : (json.items ?? [])
  },

  addAsset: (item: AssetItem) =>
    request<void>('/api/assets', { method: 'POST', body: JSON.stringify(item) }),

  removeAsset: (id: string) => request<void>(`/api/assets/${id}`, { method: 'DELETE' }),

  async listAssetLibrary(params) {
    const q = new URLSearchParams({
      page: String(params?.page ?? 1),
      perPage: String(params?.perPage ?? 100),
    })
    // 只在真的筛的时候带参数：`category=all` 也传会让后端多一次无谓的条件判断，
    // 更重要的是让 URL 干净，方便排查时一眼看出筛了什么
    if (params?.category && params.category !== 'all') q.set('category', params.category)
    if (params?.origin && params.origin !== 'all') q.set('origin', params.origin)
    if (params?.kind && params.kind !== 'all') q.set('kind', params.kind)
    if (params?.q) q.set('q', params.q)

    const json = await request<{
      items?: AssetLibraryItem[]
      total?: number
      page?: number
      perPage?: number
    }>(`/api/asset-library?${q.toString()}`)
    return {
      items: json.items ?? [],
      total: json.total ?? 0,
      page: json.page ?? 1,
      perPage: json.perPage ?? 100,
    }
  },

  assetLibraryStats: () =>
    request<AssetLibraryStats>('/api/asset-library/stats').then((s) => ({
      total: s.total ?? 0,
      works: s.works ?? 0,
      assets: s.assets ?? 0,
      // 老后端（没有这一项时）回 0 而不是 undefined：首页那张卡显示 0 也比
      // 显示 `NaN` / 空好，而且它是**可发现**的（数对不上就会来查）
      favorites: s.favorites ?? 0,
      categories: s.categories ?? [],
      origins: s.origins ?? [],
    })),

  updateAssetItem: (id: string, patch: { assetCategory?: string; favorite?: boolean }) =>
    request<void>(`/api/asset-library/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteAssetItem: (id: string) =>
    request<void>(`/api/asset-library/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /*
   * 用户自存的视频模板。四条都是普通 JSON 读写 —— 唯一的「重活」在后端：
   * 建模板时它会把 `coverUrl`（分镜九宫格的上游直链）拉回来转存，
   * 所以这个 POST 可能比别的写接口慢一点点（一次图片下行）。
   * 前端**不自己转存**：那样每个页面都要重复一遍「下载 + 传图床」，
   * 而且浏览器侧的跨域限制会让一部分上游 CDN 根本拉不动。
   */
  listVideoTemplates: () =>
    request<{ items?: CustomVideoTemplate[] }>('/api/video-templates').then((j) => j.items ?? []),

  createVideoTemplate: (req: CreateVideoTemplateRequest) =>
    request<CustomVideoTemplate>('/api/video-templates', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  updateVideoTemplate: (id: string, patch: UpdateVideoTemplateRequest) =>
    request<CustomVideoTemplate>(`/api/video-templates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteVideoTemplate: (id: string) =>
    request<void>(`/api/video-templates/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /*
   * 商品资产 —— 「一个商品一条档案」，供「主图 · 详情」页直接引用。
   *
   * 与上面那组一样是普通 JSON 读写：图片地址走的是同一个 `upload()`
   * （ImgBB → COS 那条降级链），这里只负责存取，不碰文件字节。
   */
  async listProductAssets(params: ProductAssetQuery = {}) {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.language) qs.set('language', params.language)
    if (params.platform) qs.set('platform', params.platform)
    // ⚠️ 分类这里**不能写 `if (params.category)`** —— 「未分类」的哨兵值
    // `__uncategorized__` 是真值，走得通；但「全部」是空串，跳过它也是对的。
    // 真正要小心的是**别把空串发出去**：空串到了后端等于没传，
    // 那和「只看未分类」是两回事，看不出区别但结果不同。
    if (params.category) qs.set('category', params.category)
    if (params.page) qs.set('page', String(params.page))
    if (params.perPage) qs.set('perPage', String(params.perPage))
    const suffix = qs.toString()
    const json = await request<{ items?: ProductAsset[]; total?: number }>(
      `/api/product-assets${suffix ? `?${suffix}` : ''}`,
    )
    return { items: json.items ?? [], total: json.total ?? 0 }
  },

  productAssetFacets: () => request<ProductAssetFacets>('/api/product-assets/facets'),

  getProductAsset: (assetId: string) =>
    request<ProductAsset>(`/api/product-assets/${encodeURIComponent(assetId)}`),

  createProductAsset: (req: CreateProductAssetRequest) =>
    request<ProductAsset>('/api/product-assets', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  // 批量导入可能一次几百条（后端上限 300），比普通写接口慢一点点但不是长活，
  // 所以仍走那个 20s 的默认闸门 —— 它只是一次 INSERT 事务，不是上游推理。
  bulkCreateProductAssets: (req: { items: CreateProductAssetRequest[] }) =>
    request<BulkCreateProductAssetsResponse>('/api/product-assets/bulk', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  updateProductAsset: (assetId: string, patch: UpdateProductAssetRequest) =>
    request<ProductAsset>(`/api/product-assets/${encodeURIComponent(assetId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteProductAsset: (assetId: string) =>
    request<void>(`/api/product-assets/${encodeURIComponent(assetId)}`, { method: 'DELETE' }),

  /*
   * 商品分类。注意 `update` / `delete` 的返回**不是分类本身**而是
   * `{category, affected}` —— 改名的连带影响（改掉了几件货）必须回给界面，
   * 只回分类对象的话页面没法如实告诉用户发生了什么。
   */
  listProductCategories: () =>
    request<{ items: ProductCategory[] }>('/api/product-categories'),

  createProductCategory: (req: { name: string; sortOrder?: number }) =>
    request<ProductCategory>('/api/product-categories', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  updateProductCategory: (catId: string, patch: { name?: string; sortOrder?: number }) =>
    request<RenameProductCategoryResponse>(
      `/api/product-categories/${encodeURIComponent(catId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  deleteProductCategory: (catId: string) =>
    request<RenameProductCategoryResponse>(
      `/api/product-categories/${encodeURIComponent(catId)}`,
      { method: 'DELETE' },
    ),

  async usage(): Promise<UsageSummary> {
    const json = await request<UsageSummary & { total_items?: number }>('/api/usage/summary')
    return json
  },

  /* ---------------------------------------------------------------------- */
  /* 会员体系：登录 / 会话 / 额度账本 / 后台管理                            */
  /* ---------------------------------------------------------------------- */

  /**
   * 登录。返回 token 与账号信息。
   *
   * ⚠️ **不改全局状态**（不往 localStorage 写 token）—— 只负责「问一句」。
   * 写 token 是 `AuthContext.login()` 的事。两处都写的话，将来加
   * 「记住我」这类开关时会出现「接口调通了但没记住 / 记住了但状态没更新」。
   */
  async login(account: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ account, password }),
    })
  },

  /** 退出：吊销服务端的 token（不只是本地删掉）。 */
  async logout(): Promise<void> {
    // 用 POST：退出是有副作用的动作（服务端要写库吊销），不是幂等读操作。
    await request<unknown>('/api/auth/logout', { method: 'POST' })
  },

  /** 校验 token 还有效吗，并取回**最新**余额（进站先打它）。 */
  session: () => request<AuthUser>('/api/auth/session'),

  /** 本账号的额度账本（每一笔进出账）。 */
  myLedger: (limit = 100) =>
    request<CoinLedgerItem[]>(`/api/auth/ledger?limit=${encodeURIComponent(String(limit))}`),

  /* ---------------------- 后台侧：另一套登录 ---------------------- */

  /**
   * 后台登录。**走 `/api/admin/auth/*`，token 存在另一个键里**。
   *
   * 与 `login()` 一样：只负责「问一句」，写 token 是 `AdminAuthContext` 的事。
   */
  async adminLogin(account: string, password: string): Promise<AdminLoginResponse> {
    return request<AdminLoginResponse>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ account, password }),
    })
  },

  /** 退出后台。**只吊销后台这一个 token** —— 同一个浏览器里的会员登录不受影响。 */
  async adminLogout(): Promise<void> {
    await request<unknown>('/api/admin/auth/logout', { method: 'POST' })
  },

  /** 校验后台会话是否还有效（进 `/admin` 先打它）。 */
  adminSession: () => request<AdminUser>('/api/admin/auth/session'),

  /* ---------------------------- 后台管理 ---------------------------- */

  listUpstreams: () => request<UpstreamCatalog>('/api/admin/upstreams'),

  /**
   * 改某个上游的地址 / Token / 模型。
   *
   * `patch` 里**只放真要改的字段**：没给的字段后端一律当「不动」。
   * 传空串 = 清除覆盖、回默认值；把读回来的脱敏串原样传回 = 不修改
   * （后端认得出 `••••••`，见 `runtime_config._looks_masked`）。
   *
   * 返回的是 `{id, changed}`（改了哪几个字段），**不是整份新清单** ——
   * 所以改完要重新 `listUpstreams()` 才能看到新值，别拿返回值去刷界面。
   */
  updateUpstream: (providerId: string, patch: UpstreamUpdate) =>
    request<{ id: string; changed: string[] }>(
      `/api/admin/upstreams/${encodeURIComponent(providerId)}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    ),

  /**
   * 新增一条通道。`family` 必须是接口下发过的适配器家族 ——
   * 家族背后是一段真实代码（skill 脚本 + 适配器分支），所以**配不出来**，
   * 后台只负责「在这个家族下多备一份凭据」。
   *
   * 新增的通道**默认不启用**，要再调 `activateUpstream` 才会生效。
   */
  createUpstream: (req: UpstreamCreate) =>
    request<UpstreamCatalogResult>('/api/admin/upstreams', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  /**
   * 删除一条通道。删掉**启用中**的那条是允许的（后端会把启用态落到同族剩下的
   * 第一条）；但**家族最后一条**会被 400 拒绝 —— 删完这个家族就没有生效凭据了。
   */
  deleteUpstream: (providerId: string) =>
    request<UpstreamCatalogResult>(
      `/api/admin/upstreams/${encodeURIComponent(providerId)}`,
      { method: 'DELETE' },
    ),

  /** 把 `family` 的启用通道切到 `channel`，**立刻生效**（不需要重启后端）。 */
  activateUpstream: (family: string, channel: string) =>
    request<UpstreamCatalogResult>(
      `/api/admin/upstreams/${encodeURIComponent(family)}/active`,
      { method: 'PUT', body: JSON.stringify({ channel }) },
    ),

  /** 恢复默认通道清单（清掉新增 / 删除 / 启用这三类增量，**不动**字段覆盖值）。 */
  resetUpstreamChannels: () =>
    request<UpstreamCatalogResult>('/api/admin/upstreams/reset', { method: 'POST' }),

  adminOverview: () => request<AdminOverview>('/api/admin/overview'),

  listUsers: () => request<AuthUser[]>('/api/admin/users'),

  createUser: (req: CreateUserRequest) =>
    request<AuthUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(req) }),

  updateUser: (userId: string, patch: UpdateUserRequest) =>
    request<AuthUser>(`/api/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteUser: (userId: string) =>
    request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),

  userLedger: (userId: string) =>
    request<CoinLedgerItem[]>(`/api/admin/users/${encodeURIComponent(userId)}/ledger`),

  /* ---- 后台账号（管后台的人，与会员列表是两个端点） ---- */

  listAdmins: () => request<AdminUser[]>('/api/admin/admins'),

  createAdmin: (req: CreateAdminRequest) =>
    request<AdminUser>('/api/admin/admins', { method: 'POST', body: JSON.stringify(req) }),

  updateAdmin: (adminId: string, patch: UpdateAdminRequest) =>
    request<AdminUser>(`/api/admin/admins/${encodeURIComponent(adminId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteAdmin: (adminId: string) =>
    request<{ ok: boolean }>(`/api/admin/admins/${encodeURIComponent(adminId)}`, {
      method: 'DELETE',
    }),
}

export type { OutputKind }
